import { useEffect, useRef, useState } from 'react';
import { joinSpoken } from './joinSpoken';
import { SpokenVoice, VoiceTurn } from './useVoiceTurn';

/**
 * Words spoken into the box: dictated from its mic (a tap starts, a second
 * stops), they follow what was typed and grow as they are heard; a hold
 * let go on 编辑 opens them in the editor instead. The voice of words
 * spoken into the box goes with the text when it is sent.
 */
export function useSpokenIntoBox({
  voice,
  value,
  onChangeText,
  canTalk,
  onOpen,
}: {
  voice: VoiceTurn;
  value: string;
  onChangeText: (text: string) => void;
  canTalk: boolean;
  /** Words came into the box: it shows, even from the bar. */
  onOpen: () => void;
}) {
  const [editing, setEditing] = useState<{
    text: string;
    voice: SpokenVoice;
  } | null>(null);
  const spokenVoice = useRef<SpokenVoice | null>(null);
  // The handlers read the latest values without resubscribing.
  const latest = useRef({ value, onChangeText, onOpen, canTalk });
  latest.current = { value, onChangeText, onOpen, canTalk };
  // What was in the box when dictation began: its words follow it, live.
  const base = useRef('');
  useEffect(() => {
    const live = voice.state$.subscribe(now => {
      const dictated =
        (now.kind === 'holding' && now.via === 'dictate') ||
        (now.kind === 'recognizing' && now.outcome === 'insert');
      if (dictated)
        latest.current.onChangeText(joinSpoken(base.current, now.caption));
    });
    const texts = voice.texts$.subscribe(spoken => {
      if (spoken.outcome === 'edit')
        setEditing({ text: spoken.text, voice: spoken.voice });
      else {
        spokenVoice.current = spoken.voice;
        latest.current.onChangeText(joinSpoken(base.current, spoken.text));
        latest.current.onOpen();
      }
    });
    return () => {
      live.unsubscribe();
      texts.unsubscribe();
    };
  }, [voice]);
  // Gone while dictating, nothing will stop it: it is dropped.
  useEffect(
    () => () => {
      const now = voice.state$.getValue();
      if (now.kind === 'holding' && now.via === 'dictate') voice.cancel();
    },
    [voice],
  );
  /** The mic's tap: starts dictating, or stops (the voice now, not as rendered). */
  const toggleDictation = () => {
    const now = voice.state$.getValue();
    if (now.kind === 'holding' && now.via === 'dictate')
      voice.release('insert');
    else if (latest.current.canTalk && now.kind === 'idle') {
      base.current = latest.current.value;
      voice.press('dictate');
    }
  };
  return {
    editing,
    closeEditor: () => setEditing(null),
    spokenVoice,
    toggleDictation,
  };
}
